import type { DateTime } from "luxon";
import type { ReactNode, SyntheticEvent } from "react";
import type { Nurse } from "../../../utils/models";
import type { SystemChipItem } from "../Clickable/TabsAndChips/SystemChips/SystemChips";
import type { SystemAutoCompleteOption } from "../Inputs/SystemAutoComplete/SystemAutoComplete";
import type { DropdownItem } from "../Inputs/SystemDropDown";
import type { FacetFilterOption } from "./FacetFilterSection";
import type { FilterFieldGroupFrame, FilterFieldGroupTitleVariant } from "./FilterFieldGroup";

/**
 * Shared by every field kind.
 *
 * `id` is load-bearing: it is the React key, the default test id suffix, and the only stable handle a
 * caller has on a field it built in a loop.
 */
type FilterFieldBase = {
	id: string;
	/**
	 * Drop the field from the rail entirely — for permission and feature gating (`hidden: !isSupport`).
	 *
	 * Prefer this over conditionally splicing the array: a hidden field is skipped by the loading
	 * skeleton too, so a user who will never see the control never sees a phantom placeholder for it
	 * either. Hand-written skeletons get that wrong, because nothing forces them to re-check the gate.
	 */
	hidden?: boolean;
	/** Pin the control's box to a fixed width (px) — see `FilterFieldRow`'s `width`. */
	width?: number;
	/** Overrides the id-derived test id. */
	dataTestId?: string;
};

/**
 * A checkbox facet group with live counts — `FacetFilterSection`, the Team rail's building block.
 * Its props pass straight through; see that component for what each one does.
 */
export type FacetFilterField = FilterFieldBase & {
	kind: "facet";
	/** Already-translated group title. */
	title: string;
	options: FacetFilterOption[];
	onToggle: (id: string, checked: boolean) => void;
	defaultExpanded?: boolean;
	expanded?: boolean;
	visibleLimit?: number;
	countMode?: "absolute" | "additive";
	foldZeroCounts?: boolean;
	searchable?: boolean;
	searchPlaceholder?: string;
	highlighted?: boolean;
	onExpandedChange?: (expanded: boolean) => void;
	onShowAllChange?: (showAll: boolean) => void;
};

/**
 * A `SystemDropdown`. Values are `string` because every filter dropdown in the app selects string
 * ids; a non-string-valued select is a `custom` field.
 *
 * `label` goes to the dropdown's own floating label, NOT to an external micro-label — so don't also
 * wrap it in a titled group for the same text.
 */
type SelectFilterFieldCommon = FilterFieldBase & {
	kind: "select";
	/** Already-translated field label. */
	label?: string;
	items: DropdownItem<string>[];
	/** Show the panel's loading state while options are still being fetched. */
	loading?: boolean;
	/** Hint shown in the closed trigger while nothing is selected. */
	placeholder?: string;
	/** Offer a "clear" affordance. Default true — a filter you cannot unset is a trap. */
	clearable?: boolean;
};

export type MultiSelectFilterField = SelectFilterFieldCommon & {
	multiple: true;
	value: string[];
	onChange: (values: string[]) => void;
};

export type SingleSelectFilterField = SelectFilterFieldCommon & {
	multiple?: false;
	value: string | undefined;
	onChange: (value?: string) => void;
};

/** A `SystemChips` set. Carries no label of its own, so `label` renders as the external micro-label. */
export type ChipsFilterField = FilterFieldBase & {
	kind: "chips";
	/** Already-translated micro-label rendered above the chip row. */
	label?: string;
	items: SystemChipItem<string>[];
	selected: SystemChipItem<string>[];
	onChange: (item: SystemChipItem<string>, selected: boolean) => void;
};

/**
 * A `SystemSwitch`. Its label is the switch's own inline label — a switch reads as "<label>: on/off",
 * so a micro-label above it would restate the same word twice.
 */
export type SwitchFilterField = FilterFieldBase & {
	kind: "switch";
	/** Already-translated inline label. */
	label: string;
	checked: boolean;
	onChange: (checked: boolean) => void;
	disabled?: boolean;
	/** Already-translated tooltip. Pair it with `disabled` — a control disabled without a stated reason reads as broken. */
	tooltip?: string;
	/**
	 * Decorative overlay absolutely positioned over the switch (a celebratory burst on unlock).
	 * Must be inert (`aria-hidden`, `pointerEvents: "none"`) — it sits on top of the hit area.
	 */
	adornment?: ReactNode;
};

/** A single `DateInput`. `label` goes to the input's own label. */
export type DateFilterField = FilterFieldBase & {
	kind: "date";
	/** Already-translated field label. */
	label?: string;
	value: DateTime | undefined;
	onChange: (date: DateTime | undefined) => void;
	minDate?: DateTime;
	maxDate?: DateTime;
	/** Render the clear affordance. Default true. */
	clearable?: boolean;
};

/** A `DateRangeSelector`, rendered flush (no card background/border/padding) and `compact`, so both pickers share one row of the rail. */
export type DateRangeFilterField = FilterFieldBase & {
	kind: "dateRange";
	startDate: DateTime | undefined;
	endDate: DateTime | undefined;
	onStartDateChange: (date: DateTime) => void;
	onEndDateChange: (date: DateTime) => void;
};

/** A `StaffAutoComplete` nurse picker. `label` goes to the autocomplete's own label. */
export type StaffFilterField = FilterFieldBase & {
	kind: "staff";
	/** Already-translated field label. */
	label?: string;
	nurses: SystemAutoCompleteOption<Nurse>[];
	value: SystemAutoCompleteOption<Nurse> | null;
	onChange: (event: SyntheticEvent, value: SystemAutoCompleteOption<Nurse> | null) => void;
};

/**
 * The escape hatch: any control the kinds above don't cover.
 *
 * It still earns the rail's label, width pin, gating and skeleton — the parts a hand-rolled `<Box>`
 * loses — so reaching for it costs a caller nothing structural. If you find yourself passing the same
 * `custom` field from more than one rail, that is the signal to promote it to a real kind.
 */
export type CustomFilterField = FilterFieldBase & {
	kind: "custom";
	/** Already-translated micro-label, if the rendered control has none of its own. */
	label?: string;
	render: () => ReactNode;
	/** Skeleton height (px) while loading. Defaults to the standard compact control height. */
	skeletonHeight?: number;
};

export type FilterField =
	| FacetFilterField
	| MultiSelectFilterField
	| SingleSelectFilterField
	| ChipsFilterField
	| SwitchFilterField
	| DateFilterField
	| DateRangeFilterField
	| StaffFilterField
	| CustomFilterField;

/**
 * One run of fields in a rail.
 *
 * A section with no `title` and `frame: "plain"` is just a run of fields at the rail's own rhythm —
 * which is how a flat rail (Activity Log) is expressed: sections group the config for the reader,
 * without adding chrome the user can see.
 */
export type FilterSection = {
	id: string;
	/** Already-translated section title. Omit for an unlabeled run of fields. */
	title?: string;
	frame?: FilterFieldGroupFrame;
	/** Which title treatment — the rail's shared section title (default) or a nested micro-label. */
	titleVariant?: FilterFieldGroupTitleVariant;
	/** Give the section a collapse chevron, like a facet group. */
	collapsible?: boolean;
	/** Whether a `collapsible` section starts open. Default true. */
	defaultExpanded?: boolean;
	/**
	 * How many filters are active in this section, shown beside the title.
	 *
	 * The page computes it, because only the page knows what "active" means for its own filters (an
	 * empty multi-select is no filter; a date range that still holds its default arguably isn't one
	 * either). `facet` sections don't need it — those count their own checked options.
	 */
	activeCount?: number;
	/** Drop the whole section — same gating semantics as a field's `hidden`. */
	hidden?: boolean;
	/** Override the gap between this section's fields. */
	fieldGap?: number;
	fields: FilterField[];
};

/** A field/section is rendered unless it is explicitly gated off. */
export const isVisible = <T extends { hidden?: boolean }>(item: T): boolean => !item.hidden;
